import { createClient } from '@supabase/supabase-js'

// Configurações do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://vussgslenvyztckeuyap.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZ1c3Nnc2xlbnZ5enRja2V1eWFwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQyODE5ODUsImV4cCI6MjA2OTg1Nzk4NX0.a3WlLKS1HrSCqWuG80goBsoUaUhtpRsV8mqmTAYpIAo'

// Verificações básicas de conectividade

// Verificar se as dependências estão disponíveis
console.log('🔧 Verificando dependências...');
console.log('🔧 createClient type:', typeof createClient);
console.log('🔧 createClient:', createClient);

// Criar cliente Supabase de forma simplificada
let supabase;

try {
  console.log('🚀 Criando cliente Supabase...');
  
  // Criar cliente com configuração mínima
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce'
    }
  });
  
  console.log('✅ Cliente Supabase criado com sucesso!');
  
} catch (error) {
  console.error('❌ Erro ao criar cliente Supabase:', error);
  
  // Fallback sem configurações
  try {
    console.log('🔄 Tentando fallback sem configurações...');
    supabase = createClient(supabaseUrl, supabaseAnonKey);
    console.log('✅ Cliente criado com fallback!');
  } catch (fallbackError) {
    console.error('❌ Erro no fallback:', fallbackError);
    supabase = null;
  }
}

// Fallback para cliente mock se não foi possível criar o cliente real
if (!supabase) {
  console.log('⚠️ Usando cliente mock - Supabase não disponível');
  
  // Fallback para cliente mock em caso de erro
  supabase = {
    auth: {
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signInWithPassword: () => Promise.resolve({ data: null, error: new Error('Cliente Supabase não disponível') }),
      signUp: () => Promise.resolve({ data: null, error: new Error('Cliente Supabase não disponível') }),
      signOut: () => Promise.resolve({ error: null }),
      resend: () => Promise.resolve({ data: null, error: new Error('Cliente Supabase não disponível') }),
      onAuthStateChange: (callback) => {
        console.log('Mock onAuthStateChange called');
        if (typeof callback === 'function') {
          setTimeout(() => callback('SIGNED_OUT', null), 0);
        }
        return {
          data: {
            subscription: {
              unsubscribe: () => console.log('Mock subscription unsubscribed')
            }
          }
        }
      },
      getSession: () => Promise.resolve({ data: { session: null }, error: null })
    },
    from: () => ({
      select: () => ({
        eq: () => ({
          single: () => Promise.resolve({ data: null, error: new Error('Cliente Supabase não disponível') })
        })
      })
    })
  };
}

export { supabase };

// Função para verificar se o usuário está autenticado
export const isAuthenticated = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    return !!user
  } catch (error) {
    console.error('Erro ao verificar autenticação:', error)
    return false
  }
}

// Função para obter o usuário atual
export const getCurrentUser = async () => {
  try {
    // Primeiro, verificar se há uma sessão ativa
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.log('❌ Erro ao obter sessão:', sessionError.message);
    }
    
    if (session?.user) {
      console.log('✅ Usuário obtido da sessão:', session.user.email);
      return session.user;
    }
    
    // Se não há sessão, tentar getUser()
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) {
      // Se o erro é "Auth session missing", tentar refresh
      if (error.message.includes('Auth session missing')) {
        console.log('🔄 Tentando refresh da sessão...');
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshedSession?.user && !refreshError) {
          console.log('✅ Usuário obtido após refresh:', refreshedSession.user.email);
          return refreshedSession.user;
        } else {
          console.log('❌ Refresh falhou:', refreshError?.message);
          return null; // Retornar null em vez de lançar erro
        }
      }
      console.log('❌ Erro de autenticação:', error.message);
      return null; // Retornar null para outros erros também
    }
    
    if (user) {
      console.log('✅ Usuário obtido via getUser:', user.email);
    }
    
    return user;
  } catch (error) {
    // Não mostrar erros de auth em páginas públicas do blog
    const isPublicBlogPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/blog');
    if (!isPublicBlogPage) {
      console.error('Erro ao obter usuário atual:', error)
    }
    return null
  }
}

// Função para fazer login
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Erro ao fazer login:', error)
    return { data: null, error }
  }
}

// Função para fazer cadastro
export const signUp = async (email, password, userData = {}) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Erro ao fazer cadastro:', error)
    return { data: null, error }
  }
}

// Função para fazer logout
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Erro ao fazer logout:', error)
    return { success: false, error }
  }
}

// Função para verificar se é admin
export const isAdmin = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .single()
    
    if (error) throw error
    return data?.role === 'admin'
  } catch (error) {
    console.error('Erro ao verificar se é admin:', error)
    return false
  }
}

// Função para reenviar email de confirmação
export const resendConfirmation = async (email) => {
  try {
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email: email
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Erro ao reenviar confirmação:', error)
    return { data: null, error }
  }
}